<template>
  <Navigation
    v-if="commonData?.navigation"
    :items="(commonData?.navigation as MenuItem[]) || []"
    :alt-lang-path="altLangPath"
  />
  <main>
    <slot />
  </main>
  <Footer :blocks="(commonData?.footer as FooterBlocksFragment[]) || []" />
  <NewsletterPopUp :block="newsletterBlock" />
</template>

<script setup lang="ts">
  import type { FooterBlocksFragment } from "~/lib/api-types";
  import type { MenuItem } from "~/components/Navigation.vue";

  type GetCommonsResponse = {
    navigation?: MenuItem[];
    footer?: FooterBlocksFragment[];
  };

  type GetPageableByIdResponse = {
    pageableById?: {
      full_url?: string | null;
    } | null;
  };

  const { locale } = useI18n();
  const runtimeConfig = useRuntimeConfig();
  const route = useRoute();
  const localePath = useLocalePath();
  const switchLocalePath = useSwitchLocalePath();

  // -------------------
  // Commons
  // -------------------
  const { data: commonData, error: commonError } =
    await usePersistedFetch<GetCommonsResponse>(
      "GetCommons",
      computed(() => ({})),
      {
        key: computed(() => `GetCommons:${locale.value}`),
        watch: [() => locale.value],
      }
    );

  watch(commonError, (e) => {
    if (e) {
      console.error("[commons] error:", e);
    }
  });

  const newsletterBlock = computed<FooterBlocksFragment | null>(() => {
    return (
      commonData.value?.footer?.find(
        (item) => item.__typename === "FormBlock"
      ) || null
    );
  });

  // -------------------
  // Alt language path
  // -------------------
  const altLocale = computed(() => (locale.value === "cs" ? "en" : "cs"));

  const altPageableId = computed(() =>
    route.meta.id ? String(route.meta.id) : ""
  );

  const shouldFetchAltPath = computed(() => Boolean(altPageableId.value));

  const { data: altPathData, error: altPathError } =
    await usePersistedFetch<GetPageableByIdResponse>(
      "GetPageableById",
      computed(() => ({
        id: altPageableId.value,
      })),
      {
        key: computed(
          () => `GetPageableById:${altPageableId.value}:${altLocale.value}`
        ),
        watch: [() => altPageableId.value, () => altLocale.value],
        enabled: computed(() => shouldFetchAltPath.value),
        headers: computed(() => ({
          "Content-Language": altLocale.value,
        })),
      }
    );

  watch(altPathError, (e) => {
    if (e && shouldFetchAltPath.value) {
      console.error("[altPath] error:", e);
    }
  });

  const altLangPath = computed(() => {
    const fallback =
      switchLocalePath(altLocale.value) ||
      localePath(route.fullPath, altLocale.value);

    if (!shouldFetchAltPath.value) {
      return fallback;
    }

    const fetchedPath = altPathData.value?.pageableById?.full_url;

    if (!fetchedPath) {
      return fallback;
    }

    return fetchedPath.startsWith("/") ? fetchedPath : `/${fetchedPath}`;
  });

  // -------------------
  // Head (hreflang)
  // -------------------
  const appHost = computed(() => runtimeConfig.public.appHost || "");
  const currentUrl = computed(() => appHost.value + route.path);

  useHead(
    computed(() => ({
      htmlAttrs: {
        lang: locale.value,
      },
      link: [
        {
          rel: "alternate",
          hreflang: "cs",
          href:
            locale.value === "cs"
              ? currentUrl.value
              : appHost.value + altLangPath.value,
        },
        {
          rel: "alternate",
          hreflang: "en",
          href:
            locale.value === "en"
              ? currentUrl.value
              : appHost.value + altLangPath.value,
        },
      ],
    }))
  );
</script>
